import type { DataTableRow } from "@client-shared";
import type { Row, RowSelectionState } from "@tanstack/react-table";
import * as React from "react";
import type { GroupedRowActionHelpers } from "@bcc/components/data-table/data-table.types";
import { getAllSelectableLeafRows, SelectionPopoverTrigger } from "@bcc/components/data-table/selection-popover";

interface GroupOrderSelectionPopoverProps<T extends object> {
  row: Row<DataTableRow<T>>;
  helpers: GroupedRowActionHelpers<T>;
}

/** Get leaf rows visible on the current page for a given group */
const getCurrentPageLeafRows = <T extends object>(
  row: Row<DataTableRow<T>>,
  helpers: GroupedRowActionHelpers<T>
): Row<DataTableRow<T>>[] => {
  const { pageIndex, pageSize } = helpers.table.getState().pagination;
  const expandedFlatRows = helpers.table.getExpandedRowModel?.()?.flatRows;

  if (!expandedFlatRows) {
    return getAllSelectableLeafRows(row);
  }

  const start = pageIndex * pageSize;
  const end = start + pageSize;
  const pageSlice = expandedFlatRows.slice(start, end);
  const pageRowIds = new Set(pageSlice.filter((r) => !r.getIsGrouped() && !r.original.isDisabled).map((r) => r.id));

  return getAllSelectableLeafRows(row).filter((leafRow) => pageRowIds.has(leafRow.id));
};

/** Count selected leaf rows for this group */
const countSelectedLeafRows = <T extends object>(row: Row<DataTableRow<T>>, selection: RowSelectionState): number => {
  const leafRows = getAllSelectableLeafRows(row);
  return leafRows.filter((leafRow) => selection[leafRow.id] === true).length;
};

export const GroupOrderSelectionPopover = <T extends object>({ row, helpers }: GroupOrderSelectionPopoverProps<T>) => {
  const { table, setRowSelection, maxSelectableRows } = helpers;

  const selection = table.getState().rowSelection;
  const allLeafRows = React.useMemo(() => getAllSelectableLeafRows(row), [row]);
  const selectedCount = React.useMemo(() => countSelectedLeafRows(row, selection), [row, selection]);
  const currentPageLeafRows = React.useMemo(() => getCurrentPageLeafRows(row, helpers), [row, helpers]);

  const totalSelectableCount = allLeafRows.length;

  /** Remaining slots for this group, accounting for other groups' selections.
   * Counts against the full selection map (not the filtered row model) so that
   * rows selected before a filter was applied are still counted. */
  const remainingSlots = React.useMemo(() => {
    if (!maxSelectableRows) return totalSelectableCount;
    const groupLeafIds = new Set(allLeafRows.map((leafRow) => leafRow.id));
    const otherSelectedCount = Object.keys(selection).filter((id) => selection[id] === true && !groupLeafIds.has(id)).length;
    return Math.max(0, maxSelectableRows - otherSelectedCount);
  }, [allLeafRows, selection, maxSelectableRows, totalSelectableCount]);

  const effectiveMax = Math.min(totalSelectableCount, remainingSlots);

  /** Remove this group's leaves + group row from the current selection */
  const clearGroupFromSelection = React.useCallback(
    (currentSelection: Record<string, boolean>) => {
      for (const leafRow of allLeafRows) {
        delete currentSelection[leafRow.id];
      }
      delete currentSelection[row.id];
    },
    [allLeafRows, row]
  );

  const handleApplySelection = React.useCallback(
    (mode: "all" | "currentPage" | "specific", quantity?: number) => {
      const currentSelection = { ...selection } as Record<string, boolean>;
      clearGroupFromSelection(currentSelection);

      let rowsToSelect: Row<DataTableRow<T>>[];
      switch (mode) {
        case "all":
          rowsToSelect = allLeafRows.slice(0, remainingSlots);
          break;
        case "currentPage":
          rowsToSelect = currentPageLeafRows.slice(0, remainingSlots);
          break;
        case "specific": {
          const count = Math.min(Math.max(0, quantity ?? 0), remainingSlots);
          rowsToSelect = allLeafRows.slice(0, count);
          break;
        }
        default:
          rowsToSelect = [];
      }

      for (const leafRow of rowsToSelect) {
        currentSelection[leafRow.id] = true;
      }

      setRowSelection(currentSelection);
    },
    [selection, clearGroupFromSelection, remainingSlots, allLeafRows, currentPageLeafRows, setRowSelection]
  );

  const handleClear = React.useCallback(() => {
    const currentSelection = { ...selection } as Record<string, boolean>;
    clearGroupFromSelection(currentSelection);
    setRowSelection(currentSelection);
  }, [selection, clearGroupFromSelection, setRowSelection]);

  return (
    <SelectionPopoverTrigger
      testIdComponent="GroupOrderSelectionPopover"
      selectedCount={selectedCount}
      allLeafCount={effectiveMax}
      currentPageCount={Math.min(currentPageLeafRows.length, effectiveMax)}
      maxLimit={effectiveMax}
      showMaxLabel={totalSelectableCount > effectiveMax}
      onApplySelection={handleApplySelection}
      onClear={handleClear}
    />
  );
};
